import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { albumPath, photoPath } from '@photos/shared'
import type { AlbumRecord, PhotoRecord } from '@photos/shared'
import { ApiError, admin, getToken, setToken, uploadPhoto } from '../lib/api'
import { mediaUrl } from '../lib/media'
import { RowMenu } from '../components/RowMenu'
import { ConfirmDialog, RenameDialog } from '../components/Dialogs'

type Auth = 'checking' | 'ok' | 'denied'

/** How often to re-check while something is still being tiled. */
const POLL_MS = 2500

/** Sentinel for the "create one as I upload" option in the album picker. */
const NEW_ALBUM = '__new__'

export function Admin(): JSX.Element {
  const [params, setParams] = useSearchParams()
  const [auth, setAuth] = useState<Auth>('checking')
  const [tokenInput, setTokenInput] = useState('')
  const [photos, setPhotos] = useState<PhotoRecord[]>([])
  const [albums, setAlbums] = useState<AlbumRecord[]>([])
  const [listError, setListError] = useState<string | null>(null)

  // A magic-link token in the URL is stored and then removed, so the address
  // bar (and anything that logs it) doesn't keep the credential around.
  useEffect(() => {
    const fromUrl = params.get('t')
    if (fromUrl) {
      setToken(fromUrl)
      params.delete('t')
      setParams(params, { replace: true })
    }
  }, [params, setParams])

  const refresh = useCallback(async () => {
    try {
      const [photoList, albumList] = await Promise.all([admin.list(), admin.albums.list()])
      setPhotos(photoList)
      setAlbums(albumList)
      setListError(null)
      setAuth('ok')
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setAuth('denied')
      else setListError(err instanceof Error ? err.message : String(err))
    }
  }, [])

  useEffect(() => {
    document.title = 'Admin · Photos'
    if (!getToken()) {
      setAuth('denied')
      return
    }
    void refresh()
  }, [refresh])

  // Tiling happens out of band (S3 event -> Lambda), so poll until it settles.
  const working = photos.some((p) => p.status === 'pending' || p.status === 'processing')
  useEffect(() => {
    if (auth !== 'ok' || !working) return
    const timer = setInterval(() => void refresh(), POLL_MS)
    return () => clearInterval(timer)
  }, [auth, working, refresh])

  if (auth === 'checking') {
    return (
      <main className="page page--centered">
        <span className="spinner" aria-hidden="true" />
      </main>
    )
  }

  if (auth === 'denied') {
    return (
      <main className="page page--centered">
        <h1>Admin</h1>
        <p className="muted">Paste the admin token, or open /admin?t=&lt;token&gt;.</p>
        <form
          className="token-form"
          onSubmit={(event) => {
            event.preventDefault()
            setToken(tokenInput.trim() || null)
            setAuth('checking')
            void refresh()
          }}
        >
          <input
            type="password"
            value={tokenInput}
            onChange={(event) => setTokenInput(event.target.value)}
            placeholder="Admin token"
            autoFocus
          />
          <button type="submit">Unlock</button>
        </form>
      </main>
    )
  }

  return (
    <main className="page">
      <header className="page__header page__header--row">
        <div>
          <h1>Admin</h1>
          <p className="muted">
            {albums.length} album{albums.length === 1 ? '' : 's'} · {photos.length} photo
            {photos.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="row">
          <Link className="button button--ghost" to="/">
            Gallery
          </Link>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              setToken(null)
              setAuth('denied')
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <UploadForm albums={albums} onUploaded={refresh} />

      {listError ? <p className="error">{listError}</p> : null}

      <AlbumTable albums={albums} photos={photos} onChanged={refresh} />

      <h2 className="section-heading">Photos</h2>
      <PhotoTable photos={photos} albums={albums} onChanged={refresh} />
    </main>
  )
}

function UploadForm({
  albums,
  onUploaded,
}: {
  albums: AlbumRecord[]
  onUploaded: () => Promise<void>
}): JSX.Element {
  const [title, setTitle] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [albumId, setAlbumId] = useState('')
  const [newAlbumTitle, setNewAlbumTitle] = useState('')
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Default to the newest album, or straight to "new" when there are none.
  useEffect(() => {
    if (albumId) return
    setAlbumId(albums[0]?.id ?? NEW_ALBUM)
  }, [albums, albumId])

  const creatingAlbum = albumId === NEW_ALBUM

  const accept = (chosen: File | null) => {
    setFile(chosen)
    setError(null)
    // Offer the filename as a starting title rather than making them type it.
    if (chosen && !title.trim()) {
      setTitle(chosen.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '))
    }
  }

  const ready = file && title.trim() && (creatingAlbum ? newAlbumTitle.trim() : albumId)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!ready || !file || progress !== null) return

    setProgress(0)
    setError(null)
    try {
      // Creating the album first means a failed upload still leaves the album,
      // which is recoverable; the reverse would orphan the photo.
      const target = creatingAlbum
        ? (await admin.albums.create({ title: newAlbumTitle.trim() })).id
        : albumId

      await uploadPhoto(target, title.trim(), file, setProgress)

      setTitle('')
      setFile(null)
      setNewAlbumTitle('')
      if (creatingAlbum) setAlbumId('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      await onUploaded()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setProgress(null)
    }
  }

  return (
    <form className="upload" onSubmit={submit}>
      <div
        className={`upload__drop${dragging ? ' upload__drop--active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          accept(event.dataTransfer.files[0] ?? null)
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        {file ? (
          <>
            <strong>{file.name}</strong>
            <span className="muted">{formatBytes(file.size)}</span>
          </>
        ) : (
          <>
            <strong>Drop an equirectangular photo here</strong>
            <span className="muted">or click to choose · 2:1 JPEG, any resolution</span>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => accept(event.target.files?.[0] ?? null)}
      />

      <div className="row">
        <input
          className="grow"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Title"
          maxLength={200}
        />
        <select
          value={albumId}
          onChange={(event) => setAlbumId(event.target.value)}
          aria-label="Album"
        >
          {albums.map((album) => (
            <option key={album.id} value={album.id}>
              {album.title}
              {album.published ? '' : ' (unpublished)'}
            </option>
          ))}
          <option value={NEW_ALBUM}>＋ New album…</option>
        </select>
        {creatingAlbum ? (
          <input
            value={newAlbumTitle}
            onChange={(event) => setNewAlbumTitle(event.target.value)}
            placeholder="New album name"
            maxLength={200}
          />
        ) : null}
        <button type="submit" disabled={!ready || progress !== null}>
          {progress === null ? 'Upload' : `Uploading ${Math.round(progress * 100)}%`}
        </button>
      </div>

      {progress !== null ? (
        <div className="progress">
          <div className="progress__bar" style={{ width: `${progress * 100}%` }} />
        </div>
      ) : null}

      {error ? <p className="error">{error}</p> : null}
    </form>
  )
}

/** Which dialog a table currently has open, and for what. */
type RowDialog = { kind: 'rename' | 'delete'; id: string; title: string } | null

function AlbumTable({
  albums,
  photos,
  onChanged,
}: {
  albums: AlbumRecord[]
  photos: PhotoRecord[]
  onChanged: () => Promise<void>
}): JSX.Element {
  const [dialog, setDialog] = useState<RowDialog>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [newTitle, setNewTitle] = useState('')

  const counts = useMemo(() => {
    const map = new Map<string, number>()
    for (const photo of photos) {
      if (photo.albumId) map.set(photo.albumId, (map.get(photo.albumId) ?? 0) + 1)
    }
    return map
  }, [photos])

  const run = async (id: string, action: () => Promise<unknown>) => {
    setPendingId(id)
    try {
      await action()
      await onChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err))
    } finally {
      setPendingId(null)
    }
  }

  const closeDialog = useCallback(() => setDialog(null), [])

  return (
    <>
      <h2 className="section-heading">Albums</h2>

      <form
        className="row album-create"
        onSubmit={(event) => {
          event.preventDefault()
          const title = newTitle.trim()
          if (!title) return
          setNewTitle('')
          void run('new', () => admin.albums.create({ title }))
        }}
      >
        <input
          className="grow"
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
          placeholder="New album name"
          maxLength={200}
        />
        <button type="submit" disabled={!newTitle.trim()}>
          Create album
        </button>
      </form>

      {albums.length === 0 ? <p className="muted">No albums yet.</p> : null}

      <ul className="photo-list">
        {albums.map((album) => {
          const count = counts.get(album.id) ?? 0
          return (
            <li key={album.id} className={pendingId === album.id ? 'photo photo--busy' : 'photo'}>
              <div className="photo__main">
                <span className="photo__title">{album.title}</span>

                <div className="photo__meta">
                  <code>{album.id}</code>
                  <span>
                    {count} photo{count === 1 ? '' : 's'}
                  </span>
                  {album.published ? null : (
                    <span className="badge badge--pending" title="Reachable by link, but not listed">
                      unlisted
                    </span>
                  )}
                </div>
              </div>

              <div className="photo__actions">
                {/* Unpublished only means unlisted — the link still works. */}
                <Link className="button button--ghost" to={albumPath(album.id, album.slug)}>
                  View
                </Link>

                <label className="switch" title="List this album on the home page">
                  <input
                    type="checkbox"
                    checked={album.published}
                    onChange={(event) =>
                      void run(album.id, () =>
                        admin.albums.patch(album.id, { published: event.target.checked }),
                      )
                    }
                  />
                  Published
                </label>

                <RowMenu
                  label={`Actions for ${album.title}`}
                  items={[
                    {
                      label: 'Rename…',
                      onSelect: () =>
                        setDialog({ kind: 'rename', id: album.id, title: album.title }),
                    },
                    {
                      label: 'Delete…',
                      danger: true,
                      // The API refuses to delete a non-empty album; saying so
                      // here saves a pointless round trip.
                      disabled: count > 0,
                      hint: count > 0 ? 'Move or delete its photos first' : undefined,
                      onSelect: () =>
                        setDialog({ kind: 'delete', id: album.id, title: album.title }),
                    },
                  ]}
                />
              </div>
            </li>
          )
        })}
      </ul>

      <RenameDialog
        open={dialog?.kind === 'rename'}
        title="Rename album"
        label="Album name"
        initialValue={dialog?.title ?? ''}
        onCancel={closeDialog}
        onSubmit={(title) => {
          const id = dialog?.id
          closeDialog()
          if (id) void run(id, () => admin.albums.patch(id, { title }))
        }}
      />

      <ConfirmDialog
        open={dialog?.kind === 'delete'}
        title="Delete album"
        message={`“${dialog?.title ?? ''}” will be removed. This cannot be undone.`}
        onCancel={closeDialog}
        onConfirm={() => {
          const id = dialog?.id
          closeDialog()
          if (id) void run(id, () => admin.albums.remove(id))
        }}
      />
    </>
  )
}

function PhotoTable({
  photos,
  albums,
  onChanged,
}: {
  photos: PhotoRecord[]
  albums: AlbumRecord[]
  onChanged: () => Promise<void>
}): JSX.Element {
  const [dialog, setDialog] = useState<RowDialog>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)

  const run = async (id: string, action: () => Promise<unknown>) => {
    setPendingId(id)
    try {
      await action()
      await onChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err))
    } finally {
      setPendingId(null)
    }
  }

  const closeDialog = useCallback(() => setDialog(null), [])

  if (photos.length === 0) return <p className="muted">No photos yet.</p>

  return (
    <>
      <ul className="photo-list">
        {photos.map((photo) => (
          <li key={photo.id} className={pendingId === photo.id ? 'photo photo--busy' : 'photo'}>
            <div className="photo__thumb">
              {photo.status === 'ready' ? (
                <img src={mediaUrl(`/media/${photo.id}/thumb.jpg`)} alt="" loading="lazy" />
              ) : (
                <span className="photo__placeholder" />
              )}
            </div>

            <div className="photo__main">
              <span className="photo__title">{photo.title}</span>

              <div className="photo__meta">
                <code>{photo.id}</code>
                <StatusBadge photo={photo} />
                {photo.width ? (
                  <span>
                    {photo.width.toLocaleString()} × {photo.height?.toLocaleString()} ·{' '}
                    {photo.levels?.length ?? 0} levels
                  </span>
                ) : null}
                {photo.bytes ? <span>{formatBytes(photo.bytes)}</span> : null}
              </div>

              {photo.error ? <p className="error">{photo.error}</p> : null}
            </div>

            <div className="photo__actions">
              <select
                value={photo.albumId ?? ''}
                aria-label="Album"
                title="Move to another album"
                onChange={(event) =>
                  void run(photo.id, () => admin.patch(photo.id, { albumId: event.target.value }))
                }
              >
                {albums.map((album) => (
                  <option key={album.id} value={album.id}>
                    {album.title}
                  </option>
                ))}
              </select>

              {photo.status === 'ready' ? (
                <Link className="button button--ghost" to={photoPath(photo.id, photo.slug)}>
                  View
                </Link>
              ) : null}

              <label className="switch" title="List this photo in its album">
                <input
                  type="checkbox"
                  checked={photo.published}
                  onChange={(event) =>
                    void run(photo.id, () =>
                      admin.patch(photo.id, { published: event.target.checked }),
                    )
                  }
                />
                Published
              </label>

              <RowMenu
                label={`Actions for ${photo.title}`}
                items={[
                  {
                    label: 'Rename…',
                    onSelect: () => setDialog({ kind: 'rename', id: photo.id, title: photo.title }),
                  },
                  {
                    label: 'Re-tile',
                    disabled: photo.status === 'pending',
                    hint: 'Regenerate tiles from the original',
                    onSelect: () => void run(photo.id, () => admin.reprocess(photo.id)),
                  },
                  {
                    label: 'Delete…',
                    danger: true,
                    onSelect: () => setDialog({ kind: 'delete', id: photo.id, title: photo.title }),
                  },
                ]}
              />
            </div>
          </li>
        ))}
      </ul>

      <RenameDialog
        open={dialog?.kind === 'rename'}
        title="Rename photo"
        label="Photo title"
        initialValue={dialog?.title ?? ''}
        onCancel={closeDialog}
        onSubmit={(title) => {
          const id = dialog?.id
          closeDialog()
          if (id) void run(id, () => admin.patch(id, { title }))
        }}
      />

      <ConfirmDialog
        open={dialog?.kind === 'delete'}
        title="Delete photo"
        message={`“${dialog?.title ?? ''}” and all of its tiles will be removed. This cannot be undone.`}
        onCancel={closeDialog}
        onConfirm={() => {
          const id = dialog?.id
          closeDialog()
          if (id) void run(id, () => admin.remove(id))
        }}
      />
    </>
  )
}

function StatusBadge({ photo }: { photo: PhotoRecord }): JSX.Element {
  const label =
    photo.status === 'pending'
      ? 'waiting for upload'
      : photo.status === 'processing'
        ? 'tiling…'
        : photo.status
  return <span className={`badge badge--${photo.status}`}>{label}</span>
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`
}
